import { z } from "zod";
// ---- common shapes ----------------------------------------------------------
export const Currency = z.enum(["BTC", "ETH", "SOL", "USDT", "USD"]);
// Re-export the centralized currency config so callers can do:
//   import { CURRENCY_CONFIG, configForCurrency } from "@vb/rgs-contract";
export { CURRENCY_CONFIG, configForCurrency } from "./currency-config.js";
/** Identifies which game a session/round belongs to. Operators bind a session
 *  to a game on launch; the server validates round-open requests carry the
 *  matching slug, and admin queries aggregate by this dimension. */
export const GameSlug = z.enum(["vault-breaker", "pressure-plate", "cipher-crack"]);
export const MonetaryAmount = z.number().nonnegative().finite();
export const RoundId = z.string().min(1);
export const SessionId = z.string().min(1);
// ---- POST /rgs/session/launch ----------------------------------------------
export const LaunchRequest = z.object({
    operatorId: z.string().min(1),
    playerId: z.string().min(1),
    currency: Currency,
    locale: z.string().default("en"),
    /** Which game this session is for. Defaults to the original crash game so
     *  operators using the V1 contract keep working without changes. */
    gameSlug: GameSlug.default("vault-breaker"),
});
export const LaunchResponse = z.object({
    sessionId: SessionId,
    token: z.string(),
    balance: MonetaryAmount,
    currency: Currency,
    /** Mirrors the request — clients hydrate this into store/state so each
     *  game route knows which slug it was bound to. */
    gameSlug: GameSlug,
    clientSeed: z.string(),
    serverSeedHash: z.string().length(64),
});
// ---- POST /rgs/round/open ---------------------------------------------------
export const OpenRoundRequest = z.object({
    sessionId: SessionId,
    betAmount: z.number().positive().finite(),
    clientSeed: z.string().min(1).optional(),
    /** Pressure Plate only: number of bust tiles (1..24). Ignored for other
     *  games. Server validates against the session's gameSlug. */
    mineCount: z.number().int().min(1).max(24).optional(),
});
export const OpenRoundResponse = z.object({
    roundId: RoundId,
    serverSeedHash: z.string().length(64),
    clientSeed: z.string(),
    nonce: z.number().int().nonnegative(),
    balance: MonetaryAmount,
    /** PP/CC reveal the seed at round-open so the client UX (immediate
     *  safe/mine feedback per tile, peg feedback per guess) stays interactive.
     *  Crash never sets this — the server runs the live tick stream, so the
     *  seed is held back until resolve via /rgs/round/:id/seeds.
     *
     *  Security note (V1 MVP): a sophisticated PP client could pre-compute the
     *  mine layout and never bust, breaking the 1% house edge. Acceptable for
     *  the "first paid operator integration" gate; before that ships, swap to
     *  per-pick `/rgs/round/step` + per-guess `/rgs/round/guess` endpoints so
     *  the seed only reveals after resolve. Tracked as Phase 1 follow-up. */
    serverSeed: z.string().length(64).optional(),
});
// ---- POST /rgs/round/resolve ------------------------------------------------
//
// Discriminated union on `gameSlug` — each game's resolve carries the inputs
// that game needs to verify the player's choices against the committed seed.
//   - Vault Breaker (crash): cashoutMultiplier (or absence of it = bust).
//   - Pressure Plate: which tiles the player stepped on, in order.
//   - Cipher Crack: the player's guesses + attemptUsed for payout-tier lookup.
export const ResolveRoundRequestVaultBreaker = z.object({
    sessionId: SessionId,
    roundId: RoundId,
    gameSlug: z.literal("vault-breaker"),
    cashoutMultiplier: z.number().positive().finite(),
});
export const ResolveRoundRequestPressurePlate = z.object({
    sessionId: SessionId,
    roundId: RoundId,
    gameSlug: z.literal("pressure-plate"),
    /** Indices of tiles the player stepped on, in the order pressed. The
     *  resolver verifies each was a "safe" tile against the seeded layout. */
    tilesRevealed: z.array(z.number().int().nonnegative()).max(64),
    /** True if the player chose to cash out before stepping on the bust tile. */
    cashedOut: z.boolean(),
});
export const ResolveRoundRequestCipherCrack = z.object({
    sessionId: SessionId,
    roundId: RoundId,
    gameSlug: z.literal("cipher-crack"),
    /** The player's 4-digit guesses. Empty array if they timed out / cashed out
     *  with no guesses. Server uses guesses.length to credit the right payout. */
    guesses: z.array(z.string().regex(/^[0-9]{4}$/)).max(4),
    /** True if the final guess matched the code (player wins big). */
    cracked: z.boolean(),
    /** True if the player chose to cash out early. */
    cashedOut: z.boolean(),
});
export const ResolveRoundRequest = z.discriminatedUnion("gameSlug", [
    ResolveRoundRequestVaultBreaker,
    ResolveRoundRequestPressurePlate,
    ResolveRoundRequestCipherCrack,
]);
export const ResolveRoundResponse = z.object({
    roundId: RoundId,
    outcome: z.enum(["win", "bust"]),
    cashoutMultiplier: z.number().nonnegative(),
    payout: MonetaryAmount,
    balance: MonetaryAmount,
});
// ---- POST /rgs/round/rollback -----------------------------------------------
export const RollbackRequest = z.object({
    sessionId: SessionId,
    roundId: RoundId,
    reason: z.string().optional(),
});
export const RollbackResponse = z.object({
    roundId: RoundId,
    rolledBack: z.boolean(),
    balance: MonetaryAmount,
});
// ---- GET /rgs/balance -------------------------------------------------------
export const BalanceResponse = z.object({
    sessionId: SessionId,
    balance: MonetaryAmount,
    currency: Currency,
});
// ---- GET /rgs/round/:id/seeds -----------------------------------------------
export const RoundSeedsResponse = z.object({
    roundId: RoundId,
    serverSeed: z.string().length(64),
    serverSeedHash: z.string().length(64),
    clientSeed: z.string(),
    nonce: z.number().int().nonnegative(),
    bustMultiplier: z.number(),
    revealed: z.boolean(),
});
// ---- GET /admin/stats -------------------------------------------------------
/** Per-game volume + GGR snapshot. Powers the three KPI cards on the admin
 *  Overview tab AND the per-game expansion under each operator row. */
export const PerGameKpi = z.object({
    gameSlug: GameSlug,
    rounds: z.number().int().nonnegative(),
    wagered: z.number().nonnegative().finite(),
    ggr: z.number().finite(),
    /** Empirical RTP = paid / wagered. 0 when wagered is 0. */
    rtp: z.number().finite(),
    /** Per-currency breakdown. Lets operators-of-the-platform see "0.5 BTC
     *  + 1,200 USDT wagered" without fudging into a single number. */
    byCurrency: z.record(z.string(), // Currency code; relaxed from Currency for forward-compat.
    z.object({
        wagered: z.number().nonnegative().finite(),
        paid: z.number().finite(),
    })),
});
export const AdminStats = z.object({
    operators: z.object({
        total: z.number().int().nonnegative(),
        active: z.number().int().nonnegative(),
        suspended: z.number().int().nonnegative(),
        terminated: z.number().int().nonnegative(),
    }),
    sessions: z.object({
        total: z.number().int().nonnegative(),
        activeRounds: z.number().int().nonnegative(),
    }),
    last30d: z.object({
        rounds: z.number().int().nonnegative(),
        wagered: z.number().nonnegative().finite(),
        ggr: z.number().finite(),
    }),
    /** Per-game KPI breakdown — one entry per game slug. Empty array on
     *  installs that haven't booted any rounds yet. */
    perGame: z.array(PerGameKpi).default([]),
    uptimeSec: z.number().nonnegative().finite(),
    nodeVersion: z.string(),
});
// ---- WebSocket events (server → client) -------------------------------------
export const WsServerEvent = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("round:state"),
        roundId: RoundId,
        phase: z.enum(["betting", "live", "win", "bust"]),
        startedAt: z.number().optional(),
    }),
    // Catch-up snapshot emitted to a freshly-(re)authed socket. Carries enough
    // state for the client to redraw the live multiplier without waiting for
    // the next tick (fixes the "frozen multiplier on reconnect" demo killer).
    // `idle` = no active round; `live` = mid-round; `resolved` = round just
    // finished but client missed the resolved event.
    z.object({
        type: z.literal("round:resync"),
        phase: z.enum(["idle", "live", "resolved"]),
        roundId: RoundId.optional(),
        startedAt: z.number().optional(),
        elapsedMs: z.number().nonnegative().optional(),
        currentMultiplier: z.number().optional(),
        serverSeedHash: z.string().length(64).optional(),
    }),
    z.object({
        type: z.literal("tick"),
        roundId: RoundId,
        tMs: z.number().nonnegative(),
        multiplier: z.number(),
    }),
    z.object({
        type: z.literal("crack"),
        roundId: RoundId,
        index: z.number().int().min(0).max(4),
        tMs: z.number().nonnegative(),
    }),
    z.object({
        type: z.literal("resolved"),
        roundId: RoundId,
        outcome: z.enum(["win", "bust"]),
        multiplier: z.number(),
        payout: MonetaryAmount,
    }),
    z.object({
        type: z.literal("error"),
        message: z.string(),
    }),
]);
// ---- Public-feed event (anonymized round resolutions, no auth) -------------
// Streamed on the dedicated /feed/public WS endpoint so anyone can watch the
// platform pulse. Player ids are masked to last 4 chars; full round id is
// shortened to last 8.
export const PublicFeedEvent = z.object({
    type: z.literal("feed:resolved"),
    roundIdShort: z.string(),
    playerTag: z.string(),
    operatorId: z.string(),
    /** Which game this round belongs to. Optional for back-compat with rows
     *  emitted before multi-game rollout; UI defaults to "vault-breaker". */
    gameSlug: GameSlug.optional(),
    currency: Currency,
    outcome: z.enum(["win", "bust"]),
    multiplier: z.number(),
    betAmount: MonetaryAmount,
    payout: MonetaryAmount,
    ts: z.number(),
});
// ---- WebSocket events (client → server) -------------------------------------
export const WsClientEvent = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("auth"),
        token: z.string(),
    }),
    z.object({
        type: z.literal("cashout"),
        roundId: RoundId,
    }),
]);
// ---- POST /api/contact (studio Contact Sales form) --------------------------
export const VolumeTier = z.enum(["0-5k", "5k-50k", "50k-500k", "500k+"]);
export const Timeline = z.enum(["exploring", "next-quarter", "this-quarter", "urgent"]);
export const ReferralSource = z.enum(["search", "referral", "event", "press", "other"]);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** UTM + referrer attribution captured client-side and persisted with the lead.
 *  Mirrors the export shape of `apps/web/src/lib/utm.ts` (`getAttribution()`).
 *  All fields optional so older clients (or a fresh visitor with no params) can
 *  still submit without a UTM block. */
export const Attribution = z.object({
    source: z.string().max(120).optional(),
    medium: z.string().max(120).optional(),
    campaign: z.string().max(120).optional(),
    term: z.string().max(120).optional(),
    content: z.string().max(120).optional(),
    referrer: z.string().max(500).optional(),
    firstTouch: z.string().max(120).optional(),
    lastTouch: z.string().max(120).optional(),
});
/** Optional payout-wallet payload for operators who want fast onboarding —
 *  collected behind a collapsed details/summary on the contact form. We only
 *  enforce surface-level shape here; the studio team verifies on the back end
 *  before any disbursement. */
export const PayoutWallet = z.object({
    address: z.string().min(20).max(120),
    chain: z.enum(["solana", "ethereum", "polygon"]),
    currency: z.enum(["USDT", "USDC"]),
});
export const ContactSubmission = z.object({
    operator: z.string().trim().min(2).max(120),
    name: z.string().trim().min(2).max(120),
    email: z.string().trim().max(160).regex(EMAIL_RE, "invalid email"),
    country: z.string().trim().min(2).max(80),
    volumeTier: VolumeTier,
    message: z.string().trim().min(10).max(2000),
    timeline: Timeline.optional(),
    referral: ReferralSource.optional(),
    /** UTM + referrer attribution (captured client-side, optional). */
    utm: Attribution.optional(),
    /** Optional payout wallet for fast operator onboarding. */
    payoutWallet: PayoutWallet.optional(),
    // Honeypot — should always be empty on real submissions.
    website: z.string().max(0).optional().or(z.literal("")),
});
export const ContactResponse = z.object({
    ok: z.literal(true),
    id: z.string(),
});
// ---- POST /public/booking (self-hosted intro-call request) ------------------
//
// Replaces the Calendly link from the contact-form success state. Visitors pick
// 1-5 preferred ISO timestamps from a static availability grid; the founder
// confirms one of them inside the admin notifications panel within one
// business day. Honeypot field mirrors ContactSubmission so bots fall into
// the same drop pattern.
export const BookingTopic = z.enum([
    "sandbox-walkthrough",
    "integration-deep-dive",
    "pricing",
    "compliance",
    "other",
]);
export const BookingRequest = z.object({
    name: z.string().min(2).max(120),
    email: z.string().email().max(120),
    operator: z.string().min(2).max(120),
    country: z.string().min(2).max(120),
    topic: BookingTopic,
    /** ISO-8601 timestamps the visitor would like for the call. We collect 1-5
     *  candidates so the founder can pick the one that works without bouncing
     *  emails. The grid the web client renders is locked to 14 weekdays × 4
     *  slots/day so any valid value here also lines up with our calendar. */
    timeSlots: z.array(z.string().datetime()).min(1).max(5),
    message: z.string().max(2000).optional(),
    utm: Attribution.optional(),
    /** Honeypot — should always be empty on real submissions. */
    website: z.string().max(0).optional(),
});
export const BookingResponse = z.object({
    id: z.string(),
    receivedAt: z.string().datetime(),
});
// ---- POST /api/sandbox/operator (self-serve sandbox sign-up) ---------------
//
// Lightweight intake for the "try the sandbox" CTA — separate from the full
// contact form because it only needs four fields to provision a demo operator.
// Agent 8 owns the route handler; we just ship the contract here.
export const SandboxOperatorRequest = z.object({
    companyName: z.string().trim().min(2).max(120),
    email: z.string().trim().max(160).regex(EMAIL_RE, "invalid email"),
    country: z.string().trim().min(2).max(80).optional(),
    website: z.string().trim().url().max(500).optional(),
});
export const SandboxOperatorResponse = z.object({
    ok: z.literal(true),
    operatorId: z.string(),
    apiKey: z.string(),
    sandboxUrl: z.string().url(),
});
// ---- Admin / operator management -------------------------------------------
export const OperatorStatusZ = z.enum(["active", "suspended", "terminated"]);
export const OperatorMeta = z.object({
    websiteUrl: z.string().optional(),
    contactName: z.string().optional(),
    notes: z.string().optional(),
});
export const OperatorSummary = z.object({
    id: z.string(),
    name: z.string(),
    status: OperatorStatusZ,
    billingEmail: z.string().nullable(),
    createdAt: z.number(),
    suspendedAt: z.number().nullable(),
    suspendReason: z.string().nullable(),
    ggr30d: z.number().optional(),
    rounds30d: z.number().optional(),
    /** Per-game breakdown for this operator's last-30d window. Optional —
     *  the lite operator list endpoint may omit it; the per-operator detail
     *  endpoint always populates. */
    perGame30d: z.array(PerGameKpi).optional(),
    transparencyOptin: z.boolean().optional(),
    meta: OperatorMeta.optional(),
});
export const CreateOperatorRequest = z.object({
    name: z.string().trim().min(2).max(120),
    billingEmail: z.string().email().optional(),
    websiteUrl: z.string().trim().url().max(500).optional().or(z.literal("")),
    contactName: z.string().trim().max(120).optional(),
    notes: z.string().trim().max(2000).optional(),
    fromContactId: z.string().optional(),
});
export const CreateOperatorResponse = z.object({
    operator: OperatorSummary,
    apiKey: z.string(), // plaintext, shown ONCE on create
});
export const UpdateOperatorStatusRequest = z.object({
    status: OperatorStatusZ,
    reason: z.string().optional(),
});
export const AdminContactItem = z.object({
    id: z.string(),
    ts: z.number(),
    payload: z.unknown(),
    ip: z.string(),
    status: z.enum(["new", "approved", "rejected"]),
    operatorId: z.string().nullable(),
});
export const AdminVerifyResponse = z.object({ ok: z.literal(true) });
//# sourceMappingURL=index.js.map